import router from '@/router';
import { useApi } from '@/api';
import type { ActionContext, Module } from 'vuex';
import type { RootState } from '..';
import type { GetSessionsSessionDataResponse } from '@/types/openapi-ts';
import type { TypedStoreModule } from '@/types/vuex';

let authToken: string;

let timer: NodeJS.Timeout;
let updateIntervalData: NodeJS.Timeout;
let checkIntervalData: NodeJS.Timeout;

const setLongTimeout = (callback: () => void, timeoutMs: number) => {
  if (timeoutMs > 2147483647) {
    return setTimeout(() => setLongTimeout(callback, (timeoutMs - 2147483647)), 2147483647);
  }

  return setTimeout(callback, timeoutMs);
};

export interface SessionState {
  significantActionsSeconds: number,
  counters: GetSessionsSessionDataResponse['payload']['counters'],
}

const getters = {
  significantActionsSeconds: (state: SessionState) => state.significantActionsSeconds || 0,
  affiliatesTotal: (state: SessionState) => state.counters.affiliates_pending,
  affiliatesReviews: (state: SessionState) => state.counters.affiliates_pending,
  affiliatesMonitoringExpired: (state: SessionState) => state.counters.affiliates_monitoring_expired,
  mediaBuyingPostsReviews: (state: SessionState) => state.counters.media_buying_posts_on_review,
  mediaBuyingPostsRework: (state: SessionState) => state.counters.media_buying_posts_for_rework,
  mediaBuyingPaymentsModerationReviews: (state: SessionState) => state.counters.media_buying_payments_moderation_on_review,
  mediaBuyingPaymentsModerationRework: (state: SessionState) => state.counters.media_buying_payments_moderation_for_rework,
  mediaBuyingPaymentsPayoutNotPaid: (state: SessionState) => state.counters.media_buying_payments_payout_not_paid,
  mediaBuyingPaymentsPayoutRework: (state: SessionState) => state.counters.media_buying_payments_payout_for_rework,
  mediaBuyingPaymentsPayoutProcessing: (state: SessionState) => state.counters.media_buying_payments_payout_processing,
  paymentsReviews: (state: SessionState) => state.counters.affiliates_payments_on_review,
  paymentsNotPaid: (state: SessionState) => state.counters.affiliates_payments_approved_not_paid,
  paymentsForRework: (state: SessionState) => state.counters.affiliates_payments_approved_for_rework,
};

const mutations = {
  setSessionData(state: SessionState, payload: number) {
    state.significantActionsSeconds = payload;
  },
  setSessionDataCounters(state: SessionState, payload: GetSessionsSessionDataResponse['payload']['counters']) {
    state.counters = payload;
  },
}

const actions = {
  initialSessionData({ dispatch }: ActionContext<SessionState, RootState>) {
    return dispatch('getSessionData');
  },

  async getSessionData({ dispatch, commit, state }: ActionContext<SessionState, RootState>) {
    const api = useApi();

    try {
      dispatch('clearTimers');
      const { data: { payload } } = await api.getSessionsData();
      commit('setSessionData', payload.significant_actions_remaining_seconds);
      commit('setSessionDataCounters', payload.counters);
      authToken = localStorage.getItem('auth-token')!;
      if (state.significantActionsSeconds !== 0) {
        window.$cookies.set(authToken!, state.significantActionsSeconds, state.significantActionsSeconds);
        dispatch('setTimer');
      } else {
        window.$cookies.remove(authToken);
      }
      dispatch('setUpdateIntervalData');
      dispatch('setCheckIntervalData');
    } catch (error) {
      commit('setSessionData', {});
    }
  },

  setTimer({ dispatch, commit, state }: ActionContext<SessionState, RootState>) {
    timer = setLongTimeout(() => {
      commit('setSessionData', 0);
      if (window.location.pathname.split('/')[1] === 'teams') {
        router.push('/affiliates');
      }
      dispatch('clearTimers');
    }, state.significantActionsSeconds * 1000);
  },

  setUpdateIntervalData({ dispatch }: ActionContext<SessionState, RootState>) {
    updateIntervalData = setInterval(() => {
      dispatch('updateSession');
    }, 60000);
  },
  setCheckIntervalData({ dispatch }: ActionContext<SessionState, RootState>) {
    checkIntervalData = setInterval(() => {
      dispatch('checkSession');
    }, 2000);
  },

  async updateSession({ dispatch }: ActionContext<SessionState, RootState>) {
    await dispatch('getSessionData');
  },

  lockSession({ commit }: ActionContext<SessionState, RootState>) {
    commit('setSessionData', 0);
    window.$cookies.remove(localStorage.getItem('auth-token')!);
  },
  checkSession({ dispatch, commit }: ActionContext<SessionState, RootState>) {
    dispatch('getSessionAuth');
    const significantSeconds = +window.$cookies.get(localStorage.getItem('auth-token')!);
    if (significantSeconds === 0) {
      dispatch('lockSession');
    } else {
      commit('setSessionData', significantSeconds);
    }
  },

  getSessionAuth() {
    const api = useApi();

    api.affiliatesApi.defaults.headers.common.Authorization = localStorage.getItem('auth-token')!;
    api.miscApi.defaults.headers.common.Authorization = localStorage.getItem('auth-token')!;
  },

  clearTimers() {
    clearTimeout(timer);
    clearInterval(updateIntervalData);
    clearInterval(checkIntervalData);
  },
}

const storeModule = {
  state: {
    significantActionsSeconds: 0,
    counters: {
      affiliates_pending: 0,
      affiliates_monitoring_expired: 0,
      affiliates_payments_approved_for_rework: 0,
      affiliates_payments_approved_not_paid: 0,
      affiliates_payments_on_review: 0,
      media_buying_payments_moderation_for_rework: 0,
      media_buying_payments_moderation_on_review: 0,
      media_buying_payments_payout_for_rework: 0,
      media_buying_payments_payout_not_paid: 0,
      media_buying_payments_payout_processing: 0,
      media_buying_posts_for_rework: 0,
      media_buying_posts_on_review: 0,
    },
  },
  getters,
  mutations,
  actions,
  namespaced: true,
}

export type SessionModule = TypedStoreModule<'session', typeof storeModule>;

export default storeModule;
